import { Injectable } from '@angular/core';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { saveAs } from 'file-saver';
import {
    IPlandayImportLog,
    IPlandayImportLogListParameter,
    IPlandayRoleMatch,
    IPlandaySettings,
    IPlandayUnitMatch,
    IPlandayUnitsMatchData,
    IPlandayUserMatch,
    IPlandayUsersMatchData,
} from '@fi-app/external-platform-integration/planday/planday';
import { IItemsListDataFetchResult } from '@fi-shared/fi-items-list/fi-items-list';
import { FiImageSize } from '@fi-shared/types';
import { FiPrintDownloadService } from '@fi-shared/fi-print-download/fi-print-download.service';

@Injectable()
export class PlandayService {
    constructor(
        private fiBackend: FiBackendService,
        private fiLoading: LoadingService,
        private fiPrintDownloadService: FiPrintDownloadService,
    ) {}

    getPlandaySettings(): Promise<IPlandaySettings> {
        return this.fiBackend.fetchData<IPlandaySettings>('Integration/Planday/GetPlandaySettings');
    }

    savePlandaySettings(plandaySettings: IPlandaySettings) {
        const promise = this.fiBackend.fetchDataPost<number>(
            'Integration/Planday/SavePlandaySettings',
            plandaySettings,
        );
        promise.then((plandaySettingsId) => {
            plandaySettings.Id = plandaySettingsId;
        });
        return this.fiLoading.trackPromise(promise);
    }

    getExistingRoleMatches() {
        return this.fiBackend.fetchData<IPlandayRoleMatch[]>('Integration/Planday/GetExistingRoleMatches');
    }

    getEmployeeGroupMatches() {
        const promise = this.fiBackend.fetchData<IPlandayRoleMatch[]>('Integration/Planday/GetEmployeeGroupMatches');
        return this.fiLoading.trackPromise(promise);
    }

    savePlandayRoleMatches(plandayRoleMatches: IPlandayRoleMatch[]) {
        const promise = this.fiBackend.fetchDataPost<void>(
            'Integration/Planday/SavePlandayRoleMatches',
            plandayRoleMatches,
        );
        return this.fiLoading.trackPromise(promise);
    }

    getPlandayUnitsMatchData(onlyMatched?: boolean) {
        const url = `Integration/Planday/GetPlandayUnitsMatchData?onlyMatched=${!!onlyMatched}`;
        const promise = this.fiBackend.fetchData<IPlandayUnitsMatchData>(url);
        return this.fiLoading.trackPromise(promise);
    }

    savePlandayUnitMatches(plandayUnitMatches: IPlandayUnitMatch[]) {
        const promise = this.fiBackend.fetchDataPost<void>(
            'Integration/Planday/SavePlandayUnitMatches',
            plandayUnitMatches,
        );
        return this.fiLoading.trackPromise(promise);
    }

    saveMatchUnitsWithAreas(plandayUnitMatches: IPlandayUnitMatch[]) {
        const promise = this.fiBackend.fetchDataPost<void>(
            'Integration/Planday/SaveMatchUnitsWithAreas',
            plandayUnitMatches,
        );
        return this.fiLoading.trackPromise(promise);
    }

    getPlandayUsersMatchData() {
        const promise = this.fiBackend.fetchData<IPlandayUsersMatchData>(
            'Integration/Planday/GetPlandayUsersMatchData',
        );
        return this.fiLoading.trackPromise(promise);
    }

    savePlandayUserMatches(plandayUserMatches: IPlandayUserMatch[]) {
        const promise = this.fiBackend.fetchDataPost<void>(
            'Integration/Planday/SavePlandayUserMatches',
            plandayUserMatches,
        );
        return this.fiLoading.trackPromise(promise);
    }

    importPlandayUnits(isTestMode: boolean) {
        const url = `Integration/Planday/ImportPlandayUnits?isTestMode=${!!isTestMode}`;
        return this.fiBackend.fetchDataPost<IPlandayImportLog>(url);
    }

    importPlandayUsers(isTestMode: boolean) {
        const url = `Integration/Planday/ImportPlandayUsers?isTestMode=${!!isTestMode}`;
        return this.fiBackend.fetchDataPost<IPlandayImportLog>(url);
    }

    getImportLogListData(parameter: IPlandayImportLogListParameter) {
        return this.fiBackend.fetchDataPost<IItemsListDataFetchResult<IPlandayImportLog>>(
            'Integration/Planday/GetImportLogListData',
            parameter,
        );
    }

    getImportLog(importLogId: number) {
        return this.fiLoading.trackPromise(
            this.fiBackend.fetchData<IPlandayImportLog>(`Integration/Planday/GetImportLog/${importLogId}`),
        );
    }

    getApiResponse() {
        const promise = this.fiPrintDownloadService.downloadBinaryFile('Integration/Planday/GetApiResponse', {
            fileName: 'planday_api_response.json',
            size: FiImageSize.Original,
        });
        return promise;
    }
}
